import React, { useEffect, useState, useCallback } from "react";
import {
  Table, Button, Modal, Form, DatePicker, Select, InputNumber,
  Input, Space, message, Tag, Divider, Alert, Spin, Tabs, Badge, Checkbox, Descriptions,
} from "antd";
import { PlusOutlined, FileTextOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { payments, customers as custApi } from "../api";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import { fmtCurrency, fmtDate } from "../utils/format";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const modeColor = { CASH: "green", UPI: "blue", BANK: "purple" };
const refTypeColor = { SALE: "blue", RENT: "purple", ADVANCE: "orange", REFUND: "green", ADVANCE_APPLIED: "cyan" };
const SECRET_KEY = "admin123";

export default function Payments() {
  const [data, setData]         = useState({ content: [], totalElements: 0 });
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [allCusts, setAllCusts] = useState([]);
  const [page, setPage]         = useState(0);

  // ── Filters ───────────────────────────────────────────────────
  const [q, setQ]               = useState("");
  const [filterRefType, setFilterRefType] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [viewRec, setViewRec]   = useState(null);

  const [billsLoading, setBillsLoading] = useState(false);
  const [unpaidSales, setUnpaidSales]   = useState([]);
  const [unpaidRents, setUnpaidRents]   = useState([]);
  const [activeTab, setActiveTab]       = useState("sales");
  // Multi-select: key = "SALE-id" | "RENT-id" → { type, bill }
  const [selectedBills, setSelectedBills] = useState({});

  const [editing, setEditing]         = useState(null);
  const [editModal, setEditModal]     = useState(false);
  const [secretModal, setSecretModal] = useState(null); // { action, record }
  const [secretInput, setSecretInput] = useState("");
  const [deleteRec, setDeleteRec]     = useState(null); // record pending confirm-delete

  const [form]     = Form.useForm();
  const [editForm] = Form.useForm();

  const load = useCallback(async (pg = page, search = q, refType = filterRefType, range = dateRange) => {
    setLoading(true);
    try {
      const params = {
        page: pg, size: 20,
        q: search || undefined,
        refType: refType || undefined,
        fromDate: range?.length === 2 ? range[0].format("YYYY-MM-DD") : undefined,
        toDate:   range?.length === 2 ? range[1].format("YYYY-MM-DD") : undefined,
      };
      const r = await payments.list(params);
      setData(r.data.data);
    } finally { setLoading(false); }
  }, [page, q, filterRefType, dateRange]);

  useEffect(() => { load(); }, [page]);
  useEffect(() => { custApi.all().then((r) => setAllCusts(r.data.data)); }, []);

  const handleSearch = (val) => { setQ(val); setPage(0); load(0, val, filterRefType, dateRange); };
  const handleRefType = (val) => {
    const v = val || null;
    setFilterRefType(v);
    setPage(0);
    load(0, q, v, dateRange);
  };
  const handleDateRange = (range) => { setDateRange(range || []); setPage(0); load(0, q, filterRefType, range || []); };

  const onCustomerChange = async (cid) => {
    setSelectedBills({});
    setUnpaidSales([]);
    setUnpaidRents([]);
    setActiveTab("sales");
    form.setFieldsValue({ amount: undefined });
    if (!cid) return;
    setBillsLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        payments.unpaidSales(cid),
        payments.unpaidRentals(cid),
      ]);
      const sales = sRes.data.data || [];
      const rents = rRes.data.data || [];
      setUnpaidSales(sales);
      setUnpaidRents(rents);
      // Auto-switch to tab that has bills
      if (sales.length === 0 && rents.length > 0) setActiveTab("rentals");
      else setActiveTab("sales");
    } finally {
      setBillsLoading(false);
    }
  };

  const calcBalance = ({ type, bill }) => {
    if (type === "SALE") return Number(bill.total) - Number(bill.paidAmount);
    const adv = Number(bill.advancePaid || 0);
    return Number(bill.netBill) - Number(bill.paidAmount) - adv;
  };

  const toggleBill = (type, bill) => {
    const key = `${type}-${bill.id}`;
    setSelectedBills((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { type, bill };
      return next;
    });
  };

  const selectedList  = Object.values(selectedBills);
  const selectedTypes = [...new Set(selectedList.map((s) => s.type))];
  const mixedTypes    = selectedTypes.length > 1;
  const totalBalance  = selectedList.reduce((s, item) => s + calcBalance(item), 0);
  const totalRefund   = totalBalance < 0;

  const activeSaleSelected = selectedList.length > 0 && selectedTypes.every((t) => t === "SALE");
  const activeRentSelected = selectedList.length > 0 && selectedTypes.every((t) => t === "RENT");
  const canSubmit =
    selectedList.length > 0 &&
    !mixedTypes &&
    ((activeTab === "sales"   && activeSaleSelected && unpaidSales.length > 0) ||
     (activeTab === "rentals" && activeRentSelected && unpaidRents.length > 0));

  const onTabChange = (key) => {
    setActiveTab(key);
    setSelectedBills({});
    form.setFieldsValue({ amount: undefined });
  };

  // Keep amount in sync with total balance
  useEffect(() => {
    form.setFieldsValue({
      amount: selectedList.length > 0
        ? parseFloat(Math.abs(totalBalance).toFixed(2))
        : undefined,
    });
  }, [selectedBills]);

  const onSave = async () => {
    if (!canSubmit) return;
    try {
      const vals = await form.validateFields();
      const enteredAmount = Number(vals.amount || 0);

      // Build the list of (type, bill, amountToApply) — honoring the amount the
      // user actually typed, instead of always forcing the full balance.
      let allocations;
      if (selectedList.length === 1) {
        // Single bill: pay exactly what was entered (supports partial payment).
        allocations = [{ ...selectedList[0], amountToApply: enteredAmount }];
      } else {
        // Multiple bills: apply the entered amount sequentially, each bill capped
        // at its own outstanding balance, so a partial total doesn't silently
        // settle every bill in full.
        let remaining = enteredAmount;
        allocations = selectedList.map((item) => {
          const bal = calcBalance(item);
          const billNeeds = Math.abs(bal); // amount still needed to fully settle this bill
          const apply = Math.min(remaining, billNeeds);
          remaining = Math.max(0, remaining - apply);
          return { ...item, amountToApply: apply };
        }).filter((a) => a.amountToApply > 0);
      }

      await Promise.all(
        allocations.map(({ type, bill, amountToApply }) => {
          const bal     = calcBalance({ type, bill });
          // REFUND only applies when advance already exceeds the bill (bal < 0) —
          // i.e. paying it down doesn't change that this is "money back," not "cash in."
          const refType = bal < 0 ? "REFUND" : type;
          return payments.record({
            customerId:  vals.customerId,
            paymentDate: vals.paymentDate?.format("YYYY-MM-DD"),
            mode:        vals.mode,
            notes:       vals.notes,
            refType,
            refId:       bill.id,
            reference:   type === "SALE" ? bill.saleNo : bill.returnNo,
            amount:      parseFloat(amountToApply.toFixed(2)),
          });
        })
      );
      message.success(
        allocations.length === 1 ? "Payment recorded" : `${allocations.length} payments recorded`
      );
      setModal(false);
      form.resetFields();
      setSelectedBills({});
      setUnpaidSales([]);
      setUnpaidRents([]);
      load();
    } catch { message.error("Error recording payment"); }
  };

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ paymentDate: dayjs(), mode: "CASH" });
    setSelectedBills({});
    setUnpaidSales([]);
    setUnpaidRents([]);
    setActiveTab("sales");
    setModal(true);
  };

  // Secret gate: action = 'edit' | 'delete'
  const requireSecret = (action, record) => {
    setSecretInput("");
    setSecretModal({ action, record });
  };

  const confirmSecret = () => {
    if (secretInput !== SECRET_KEY) { message.error("Incorrect secret key"); return; }
    const { action, record } = secretModal;
    setSecretModal(null);
    if (action === "edit") {
      setEditing(record);
      editForm.setFieldsValue({
        amount:      Number(record.amount),
        mode:        record.mode,
        paymentDate: record.paymentDate ? dayjs(record.paymentDate) : dayjs(),
        reference:   record.reference,
        notes:       record.notes,
      });
      setEditModal(true);
    } else if (action === "delete") {
      setDeleteRec(record);
    }
  };

  const confirmDelete = async () => {
    try {
      await payments.delete(deleteRec.id);
      message.success(`Payment ${deleteRec.paymentNo} deleted`);
      setDeleteRec(null);
      load();
    } catch { message.error("Error deleting payment"); }
  };

  const onEditSave = async () => {
    try {
      const vals = await editForm.validateFields();
      await payments.update(editing.id, { ...vals, paymentDate: vals.paymentDate?.format("YYYY-MM-DD") });
      message.success("Payment updated");
      setEditModal(false);
      setEditing(null);
      load();
    } catch { message.error("Error updating payment"); }
  };

  const cols = [
    { title: "Payment No", dataIndex: "paymentNo", render: (v) => <span className="mono text-info">{v}</span> },
    { title: "Customer", dataIndex: ["customer", "name"] },
    { title: "Date", dataIndex: "paymentDate", render: fmtDate },
    { title: "Amount", dataIndex: "amount", render: (v) => <span className="mono text-success">{fmtCurrency(v)}</span> },
    { title: "Mode", dataIndex: "mode", render: (v) => <Tag color={modeColor[v] || "default"}>{v}</Tag> },
    {
      title: "Against",
      render: (_, r) => r.refType ? (
        <Tag color={refTypeColor[r.refType] || "default"}>{r.refType} · {r.reference}</Tag>
      ) : <span style={{ color: "var(--text-muted)" }}>—</span>,
    },
    { title: "Notes", dataIndex: "notes", render: (v) => v || "—" },
    {
      title: "Action", key: "action",
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setViewRec(r)}>View</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => requireSecret("edit", r)}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => requireSecret("delete", r)}>Delete</Button>
        </Space>
      ),
    },
  ];

  const BillCard = ({ type, bill }) => {
    const key      = `${type}-${bill.id}`;
    const checked  = !!selectedBills[key];
    const balance  = calcBalance({ type, bill });
    const isRefund = balance < 0;
    const advance  = type === "RENT" ? Number(bill.advancePaid || 0) : 0;
    return (
      <div
        onClick={() => toggleBill(type, bill)}
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 12px", marginBottom: 6, borderRadius: 6, cursor: "pointer",
          border: `1px solid ${checked ? "var(--accent,#1677ff)" : isRefund ? "var(--success,#52c41a)" : "var(--border,#e8e8e8)"}`,
          background: checked ? "#e6f4ff" : isRefund ? "rgba(82,196,26,0.06)" : "var(--surface2,#fafafa)",
          transition: "all 0.15s",
        }}
      >
        <Space align="center">
          <Checkbox checked={checked} onChange={() => toggleBill(type, bill)} onClick={(e) => e.stopPropagation()} />
          <div>
            <span className="mono" style={{ fontWeight: 600 }}>
              {type === "SALE" ? bill.saleNo : bill.returnNo}
            </span>
            <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 8 }}>
              {fmtDate(type === "SALE" ? bill.saleDate : bill.returnDate)}
            </span>
            <StatusBadge status={bill.status} style={{ marginLeft: 8 }} />
            {isRefund && <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>REFUND DUE</Tag>}
          </div>
        </Space>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {type === "SALE"
              ? `Total ${fmtCurrency(bill.total)} · Paid ${fmtCurrency(bill.paidAmount)}`
              : `Bill ${fmtCurrency(bill.netBill)} · Paid ${fmtCurrency(bill.paidAmount)}${advance > 0 ? ` · Adv ${fmtCurrency(advance)}` : ""}`}
          </div>
          <div className="mono" style={{ fontWeight: 700, color: isRefund ? "var(--success)" : "var(--danger)" }}>
            {isRefund ? `Refund: ${fmtCurrency(Math.abs(balance))}` : `Balance: ${fmtCurrency(balance)}`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Record and track customer payments"
        actions={[<Button key="a" type="primary" icon={<PlusOutlined />} onClick={openModal}>Record Payment</Button>]}
      />

      {/* ── Filters ──────────────────────────────────────── */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search payment no, customer, reference..."
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <Select
          placeholder="All Types"
          allowClear
          value={filterRefType}
          onChange={handleRefType}
          style={{ width: 140 }}
          options={[
            { value: "",       label: "All Types" },
            { value: "SALE",   label: "Sale" },
            { value: "RENT",   label: "Rent" },
            { value: "ADVANCE",label: "Advance" },
            { value: "REFUND", label: "Refund" },
            { value: "ADVANCE_APPLIED", label: "Advance Applied" },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={handleDateRange}
          style={{ width: 260 }}
        />
      </Space>

      <Table
        scroll={{ x: "max-content" }} columns={cols} dataSource={data.content}
        rowKey="id" loading={loading} size="small"
        pagination={{ total: data.totalElements, pageSize: 20, current: page + 1, onChange: (p) => setPage(p - 1) }}
      />

      {/* ── View Payment Modal ───────────────────────────── */}
      <Modal
        open={!!viewRec}
        title={`Payment — ${viewRec?.paymentNo}`}
        onCancel={() => setViewRec(null)}
        footer={<Button onClick={() => setViewRec(null)}>Close</Button>}
        width={480}
      >
        {viewRec && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Payment No">{viewRec.paymentNo}</Descriptions.Item>
            <Descriptions.Item label="Customer">{viewRec.customer?.name} ({viewRec.customer?.phone})</Descriptions.Item>
            <Descriptions.Item label="Date">{fmtDate(viewRec.paymentDate)}</Descriptions.Item>
            <Descriptions.Item label="Amount"><span className="mono text-success">{fmtCurrency(viewRec.amount)}</span></Descriptions.Item>
            <Descriptions.Item label="Mode"><Tag color={modeColor[viewRec.mode] || "default"}>{viewRec.mode}</Tag></Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={refTypeColor[viewRec.refType] || "default"}>{viewRec.refType || "—"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reference">{viewRec.reference || "—"}</Descriptions.Item>
            <Descriptions.Item label="Notes">{viewRec.notes || "—"}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* ── Record Payment Modal ─────────────────────── */}
      <Modal
        open={modal} title="Record Payment" onCancel={() => setModal(false)}
        onOk={onSave}
        okButtonProps={{ disabled: !canSubmit }}
        okText={selectedList.length > 1 ? `Save ${selectedList.length} Payments` : "Save Payment"}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="customerId" label="Customer" rules={[{ required: true }]}>
            <Select
              showSearch placeholder="Select customer"
              options={allCusts.map((c) => ({ value: c.id, label: `${c.name} (${c.phone})` }))}
              onChange={onCustomerChange}
            />
          </Form.Item>

          {billsLoading && <div style={{ textAlign: "center", padding: 16 }}><Spin /></div>}

          {!billsLoading && (unpaidSales.length > 0 || unpaidRents.length > 0) && (
            <>
              <Divider style={{ borderColor: "var(--border)", margin: "8px 0 12px" }}>
                <FileTextOutlined style={{ marginRight: 6 }} />
                Outstanding Bills — tick to select, multi-select allowed within same type
              </Divider>

              <Tabs
                size="small" activeKey={activeTab} onChange={onTabChange}
                items={[
                  {
                    key: "sales",
                    label: <span>Sales <Badge count={unpaidSales.length} size="small" /></span>,
                    children: unpaidSales.length === 0 ? (
                      <Alert type="info" message="No unpaid sales — Sales payment is not applicable for this customer." style={{ marginBottom: 8 }} />
                    ) : (
                      unpaidSales.map((s) => <BillCard key={s.id} type="SALE" bill={s} />)
                    ),
                  },
                  {
                    key: "rentals",
                    label: <span>Rental Returns <Badge count={unpaidRents.length} size="small" /></span>,
                    children: unpaidRents.length === 0 ? (
                      <Alert type="info" message="No unpaid rental returns — Rental payment is not applicable for this customer." style={{ marginBottom: 8 }} />
                    ) : (
                      unpaidRents.map((r) => <BillCard key={r.id} type="RENT" bill={r} />)
                    ),
                  },
                ]}
              />

              {/* Selection summary */}
              {selectedList.length > 0 && (
                <Alert
                  type={mixedTypes ? "error" : totalRefund ? "warning" : "success"}
                  style={{ marginTop: 8, marginBottom: 4 }}
                  message={
                    mixedTypes
                      ? "Cannot mix Sale and Rental bills in one batch. Select bills of the same type only."
                      : (
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                          {selectedList.length} bill{selectedList.length > 1 ? "s" : ""} selected &nbsp;·&nbsp;
                          {totalRefund
                            ? <>Total refund: <strong style={{ color: "var(--success)" }}>{fmtCurrency(Math.abs(totalBalance))}</strong></>
                            : <>Total due: <strong style={{ color: "var(--danger)" }}>{fmtCurrency(totalBalance)}</strong></>
                          }
                          &nbsp;·&nbsp;<span style={{ color: "var(--text-muted)" }}>One payment record per bill</span>
                        </span>
                      )
                  }
                />
              )}

              {/* Disabled reason — only show when selection is valid but tab mismatch */}
              {selectedList.length > 0 && !mixedTypes && !canSubmit && (
                <Alert
                  type="warning" style={{ marginTop: 4, marginBottom: 4 }}
                  message="Selected bills don't match the active tab. Switch to the correct tab or clear selection."
                />
              )}

              <Divider style={{ borderColor: "var(--border)", margin: "12px 0 8px" }} />
            </>
          )}

          <Space wrap>
            <Form.Item
              name="amount" label={totalRefund ? "Refund Amount (₹)" : "Amount (₹)"}
              rules={[{ required: true }]}
              extra={
                !totalRefund && selectedList.length > 0 ? (
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    Defaults to full balance ({fmtCurrency(Math.abs(totalBalance))}) — edit for a partial payment
                  </span>
                ) : null
              }
            >
              <InputNumber
                min={0.01} prefix={totalRefund ? "↩ ₹" : "₹"}
                style={{ width: 160, borderColor: totalRefund ? "var(--success)" : undefined }}
              />
            </Form.Item>
            <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}>
              <DatePicker defaultValue={dayjs()} />
            </Form.Item>
            <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
              <Select style={{ width: 120 }} options={["CASH", "UPI", "BANK"].map((m) => ({ value: m, label: m }))} />
            </Form.Item>
          </Space>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional note applied to all payments in this batch" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Secret Key Modal ─────────────────────────── */}
      <Modal open={!!secretModal} title="Enter Secret Key"
        onCancel={() => setSecretModal(null)} onOk={confirmSecret} okText="Unlock" width={360}
      >
        <p style={{ color: "var(--text-muted)", marginBottom: 8 }}>
          {secretModal?.action === "delete"
            ? "Enter the secret key to delete this payment record."
            : "Enter the secret key to edit this payment record."}
        </p>
        <Input.Password
          placeholder="Secret key" value={secretInput}
          onChange={(e) => setSecretInput(e.target.value)} onPressEnter={confirmSecret}
          style={{ marginTop: 4 }}
        />
      </Modal>

      {/* ── Delete Confirm Modal ─────────────────────── */}
      <Modal
        open={!!deleteRec}
        title={<span style={{ color: "#f5222d" }}><DeleteOutlined style={{ marginRight: 8 }} />Confirm Delete</span>}
        onCancel={() => setDeleteRec(null)}
        onOk={confirmDelete}
        okText="Yes, Delete"
        okButtonProps={{ danger: true }}
        width={420}
      >
        <p>Delete payment <strong>{deleteRec?.paymentNo}</strong> — <strong>{fmtCurrency(deleteRec?.amount)}</strong>?</p>
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="This reverses the payment: customer outstanding is restored and any linked rental return balance is updated."
        />
      </Modal>

      {/* ── Edit Payment Modal ───────────────────────── */}
      <Modal
        open={editModal} title={`Edit Payment — ${editing?.paymentNo}`}
        onCancel={() => { setEditModal(false); setEditing(null); }} onOk={onEditSave} okText="Save Changes" width={480}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
            <InputNumber min={0} prefix="₹" style={{ width: "100%" }} />
          </Form.Item>
          <Space wrap>
            <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker /></Form.Item>
            <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
              <Select style={{ width: 120 }} options={["CASH", "UPI", "BANK"].map((m) => ({ value: m, label: m }))} />
            </Form.Item>
          </Space>
          <Form.Item name="reference" label="Reference"><Input /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          {editing?.refType === "ADVANCE" && (
            <Alert type="warning"
              message="This is an ADVANCE payment. Editing the amount here updates the payment record only — make sure the rental's advance amount matches."
              style={{ marginTop: 4 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
}